// src/pages/teacher/TeacherBudgets.tsx
// ניהול תקציבים — כרטיסי מקור + progress bars + יצירה

import { useState, useEffect } from 'react';
import { Wallet, Plus, X, Calendar, TrendingDown, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { teacherApi } from '../../services/api';
import { useTeacherStore, BudgetSource, sourceTypeNames } from '../../stores/teacherStore';
import { useAuthStore } from '../../stores/authStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { teacherBudgetHelpTexts } from '../../constants/helpTexts';
import DeleteConfirmModal from '../../components/shared/DeleteConfirmModal';
import { teacherColors as colors } from '../../styles/colors';

function getBudgetColor(remaining: number, total: number) {
  if (total === 0) return { bg: colors.border, text: colors.textLight, bar: colors.border };
  const pct = remaining / total;
  if (pct > 0.5) return { bg: colors.greenLight, text: '#166534', bar: colors.green };
  if (pct > 0.2) return { bg: colors.yellowLight, text: '#92400e', bar: colors.yellow };
  return { bg: colors.redLight, text: '#991b1b', bar: colors.red };
}

const sourceTypeOptions = [
  { value: 1, label: 'תקציב רשת' },
  { value: 2, label: 'תקציב עירוני' },
  { value: 3, label: 'ועד הורים' },
  { value: 4, label: 'משרד החינוך' },
  { value: 5, label: 'תרומה' },
  { value: 6, label: 'אחר' },
];

export default function TeacherBudgets() {
  const { budgetSources, setBudgetSources, isLoadingBudgets, setLoading } = useTeacherStore();
  const { user } = useAuthStore();
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Create form state
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState(1);
  const [newBudget, setNewBudget] = useState('');
  const [newPeriodStart, setNewPeriodStart] = useState('');
  const [newPeriodEnd, setNewPeriodEnd] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ publicId: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadBudgets();
  }, []);

  const loadBudgets = async () => {
    setLoading('budgets', true);
    try {
      const res = await teacherApi.getBudgetSources();
      if (res.success && res.data) {
        setBudgetSources(res.data);
      }
    } catch (err) {
      console.error('Failed to load budgets:', err);
    } finally {
      setLoading('budgets', false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newBudget || !newPeriodStart || !newPeriodEnd) {
      setError('יש למלא את כל השדות החובה');
      return;
    }

    setCreating(true);
    setError('');
    try {
      const res = await teacherApi.createBudgetSource({
        kindergartenPublicId: user?.kindergartenPublicId,
        name: newName.trim(),
        sourceType: newType,
        totalBudget: parseFloat(newBudget),
        periodStart: newPeriodStart,
        periodEnd: newPeriodEnd,
        notes: newNotes.trim() || undefined,
      });
      if (res.success) {
        setShowCreate(false);
        resetForm();
        await loadBudgets();
      } else {
        setError(res.message || 'שגיאה ביצירה');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה ביצירה');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewName('');
    setNewType(1);
    setNewBudget('');
    setNewPeriodStart('');
    setNewPeriodEnd('');
    setNewNotes('');
    setError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await teacherApi.deleteBudgetSource(deleteTarget.publicId);
      if (res.success) { setDeleteTarget(null); await loadBudgets(); }
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (n: number) => `${n.toLocaleString('he-IL')}₪`;
  const formatDate = (s: string) => {
    try { return new Date(s).toLocaleDateString('he-IL'); } catch { return s; }
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0 }}>
          תקציבים
        </h1>
        <button
          onClick={() => { setShowCreate(true); resetForm(); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 16px', backgroundColor: colors.primary, color: 'white',
            border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
          }}
        >
          <Plus size={18} />
          מקור חדש
        </button>
      </div>

      {/* Summary Bar */}
      {budgetSources.length > 0 && (() => {
        const active = budgetSources.filter(s => s.isActive);
        const totalBudget = active.reduce((s, b) => s + b.totalBudget, 0);
        const totalSpent = active.reduce((s, b) => s + b.totalSpent, 0);
        const totalRemaining = totalBudget - totalSpent;
        const pct = totalBudget > 0 ? (totalRemaining / totalBudget) * 100 : 0;
        const c = getBudgetColor(totalRemaining, totalBudget);
        return (
          <div style={{
            backgroundColor: colors.white, borderRadius: '14px', padding: '16px',
            border: `1px solid ${colors.border}`, marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', color: colors.textLight }}>{active.length} מקורות פעילים</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: c.text }}>
                נותר: {formatCurrency(totalRemaining)}
              </span>
            </div>
            <div style={{ height: '8px', borderRadius: '4px', backgroundColor: colors.border, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '4px', backgroundColor: c.bar,
                width: `${Math.min(pct, 100)}%`, transition: 'width 0.3s',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
              <span style={{ fontSize: '12px', color: colors.textLight }}>
                שימוש: {formatCurrency(totalSpent)}
              </span>
              <span style={{ fontSize: '12px', color: colors.textLight }}>
                סה״כ: {formatCurrency(totalBudget)}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Loading */}
      {isLoadingBudgets && (
        <div style={{ textAlign: 'center', padding: '32px', color: colors.textLight }}>
          טוען תקציבים...
        </div>
      )}

      {/* Budget Cards */}
      {!isLoadingBudgets && budgetSources.map((bs) => {
        const c = getBudgetColor(bs.remainingBudget, bs.totalBudget);
        const pct = bs.totalBudget > 0 ? (bs.remainingBudget / bs.totalBudget) * 100 : 0;
        const isExpanded = expandedId === bs.publicId;

        return (
          <div key={bs.publicId} style={{
            backgroundColor: colors.white, borderRadius: '14px',
            border: `1px solid ${colors.border}`, marginBottom: '12px', overflow: 'hidden',
            opacity: bs.isActive ? 1 : 0.6,
          }}>
            {/* Card Header */}
            <div
              onClick={() => setExpandedId(isExpanded ? null : bs.publicId)}
              style={{ padding: '16px', cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, margin: 0 }}>
                    {bs.name}
                  </h3>
                  <span style={{
                    display: 'inline-block', marginTop: '4px',
                    fontSize: '11px', padding: '2px 8px', borderRadius: '6px',
                    backgroundColor: colors.primaryLight, color: colors.primary, fontWeight: '500',
                  }}>
                    {bs.sourceTypeName || sourceTypeNames[bs.sourceType] || 'אחר'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{
                    fontSize: '14px', fontWeight: '600', color: c.text,
                    backgroundColor: c.bg, padding: '4px 10px', borderRadius: '8px',
                  }}>
                    {formatCurrency(bs.remainingBudget)}
                  </span>
                  {isExpanded ? <ChevronUp size={16} color={colors.textLight} /> : <ChevronDown size={16} color={colors.textLight} />}
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ height: '6px', borderRadius: '3px', backgroundColor: colors.border, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '3px', backgroundColor: c.bar,
                  width: `${Math.min(pct, 100)}%`, transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                <span style={{ fontSize: '11px', color: colors.textLight }}>
                  {formatCurrency(bs.totalSpent)} / {formatCurrency(bs.totalBudget)}
                </span>
                <span style={{ fontSize: '11px', color: colors.textLight }}>
                  {Math.round(pct)}% נותר
                </span>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div style={{
                padding: '0 16px 16px', borderTop: `1px solid ${colors.border}`,
                paddingTop: '12px',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Calendar size={14} color={colors.textLight} />
                    <span style={{ fontSize: '12px', color: colors.textLight }}>
                      {formatDate(bs.periodStart)} - {formatDate(bs.periodEnd)}
                    </span>
                  </div>
                  {bs.maxSingleExpense && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertCircle size={14} color={colors.textLight} />
                      <span style={{ fontSize: '12px', color: colors.textLight }}>
                        מקס׳ להוצאה: {formatCurrency(bs.maxSingleExpense)}
                      </span>
                    </div>
                  )}
                </div>
                {bs.notes && (
                  <p style={{ fontSize: '12px', color: colors.textLight, margin: '0 0 12px' }}>
                    {bs.notes}
                  </p>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget({ publicId: bs.publicId, name: bs.name }); }}
                  style={{
                    fontSize: '12px', color: colors.red, backgroundColor: 'transparent',
                    border: `1px solid ${colors.redLight}`, borderRadius: '8px',
                    padding: '6px 12px', cursor: 'pointer',
                  }}
                >
                  מחק מקור
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Empty State */}
      {!isLoadingBudgets && budgetSources.length === 0 && (
        <div style={{
          backgroundColor: colors.white, borderRadius: '14px', padding: '48px 24px',
          border: `1px solid ${colors.border}`, textAlign: 'center',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            backgroundColor: colors.primaryLight, display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <Wallet size={28} color={colors.primary} />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            אין מקורות תקציב עדיין
          </h3>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            הוסיפי מקור תקציב ראשון (רשת, עירייה, ועד הורים)
          </p>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000,
        }} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{
            backgroundColor: colors.white, borderRadius: '20px 20px 0 0',
            width: '100%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto',
            padding: '24px',
          }} onMouseDown={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, margin: 0 }}>
                מקור תקציב חדש
              </h2>
              <button onClick={() => setShowCreate(false)} style={{
                backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                padding: '4px', borderRadius: '8px',
              }}>
                <X size={22} color={colors.textLight} />
              </button>
            </div>

            {error && (
              <div style={{
                backgroundColor: colors.redLight, color: '#991b1b',
                padding: '10px 14px', borderRadius: '10px', marginBottom: '16px', fontSize: '13px',
              }}>
                {error}
              </div>
            )}

            {/* Form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <FormLabel text="שם מקור" required helpText={teacherBudgetHelpTexts.name.tooltip} style={{ color: colors.text }} />
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="לדוגמה: תקציב רשת Q1"
                  style={inputStyle}
                />
              </div>

              <div>
                <FormLabel text="סוג מקור" required helpText={teacherBudgetHelpTexts.sourceType.tooltip} style={{ color: colors.text }} />
                <select value={newType} onChange={e => setNewType(+e.target.value)} style={inputStyle}>
                  {sourceTypeOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <FormLabel text="סכום תקציב" required helpText={teacherBudgetHelpTexts.totalBudget.tooltip} style={{ color: colors.text }} />
                <input
                  type="number"
                  value={newBudget}
                  onChange={e => setNewBudget(e.target.value)}
                  placeholder="0.00"
                  style={inputStyle}
                />
                <HelperText text={teacherBudgetHelpTexts.totalBudget.helper!} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <FormLabel text="מתאריך" required helpText={teacherBudgetHelpTexts.periodStart.tooltip} style={{ color: colors.text }} />
                  <input
                    type="date"
                    value={newPeriodStart}
                    onChange={e => setNewPeriodStart(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <FormLabel text="עד תאריך" required helpText={teacherBudgetHelpTexts.periodEnd.tooltip} style={{ color: colors.text }} />
                  <input
                    type="date"
                    value={newPeriodEnd}
                    onChange={e => setNewPeriodEnd(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <FormLabel text="הערות" helpText={teacherBudgetHelpTexts.notes.tooltip} style={{ color: colors.text }} />
                <textarea
                  value={newNotes}
                  onChange={e => setNewNotes(e.target.value)}
                  placeholder="הערות נוספות..."
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              <button
                onClick={handleCreate}
                disabled={creating}
                style={{
                  width: '100%', padding: '14px', backgroundColor: creating ? colors.border : colors.primary,
                  color: 'white', border: 'none', borderRadius: '12px',
                  fontSize: '15px', fontWeight: '600', cursor: creating ? 'not-allowed' : 'pointer',
                  marginTop: '4px',
                }}
              >
                {creating ? 'יוצר...' : 'צור מקור תקציב'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        itemName={deleteTarget?.name ?? ''}
        isLoading={deleting}
      />
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '14px',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  outline: 'none',
  backgroundColor: '#f9fafb',
  boxSizing: 'border-box',
  direction: 'rtl',
};
